// "Anschrift korrigieren" (ST-PTY-002; CM-PTY-006 "versioniert"; ADR-0056
// Reviewpunkt "erste Korrektur einer Fehlerfassung"). Eine Tippfehler-Korrektur
// ist kein Umzug: sie legt eine NEUE Fassung derselben Anschrift an, und alle
// Zwecke zeigen danach die korrigierte. Beenden und neu erfassen bleibt der Weg
// fuer einen Umzug.
//
// Eine zurueckhaltende Zeilenaktion; das Formular erscheint erst auf
// Anforderung, vorbefuellt mit der gespeicherten Fassung (5.2 Nr. 9). Beim
// Oeffnen steht der Fokus im ersten Feld, beim Abbrechen wieder auf dem Knopf
// (WCAG 2.4.3). Die Pflichtangaben prueft der Server; die Feldmeldungen
// stehen am Feld (NFR-UX-005).

import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { Body1, Button, Caption1, Field, Input, makeStyles, Select } from '@fluentui/react-components';

import {
  correctionInputOf,
  usageLabel,
  countryOptions,
  POSTAL_CODE_REQUIRED_COUNTRY,
  validationMessageFor,
  type AddressGroup,
  type AddressInput,
} from '../addresses.ts';
import type { FieldError } from '../command-client.ts';
import { usePflegeStyles } from '../shell/pflegeabschnitt.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

// Das native select nimmt die Breite seiner laengsten Option; ohne Grenze ragt es schmal ueber die Karte.
const useStyles = makeStyles({
  land: { width: '100%', maxWidth: '100%', minWidth: 0, '& select': { maxWidth: '100%' } },
});

export type KorrekturEingabe = Omit<AddressInput, 'usageCode'>;

export function AnschriftKorrigieren({
  name,
  group,
  offen,
  busy,
  zuruecktreten,
  feldfehler,
  onOeffnen,
  onAbbrechen,
  onSpeichern,
}: {
  /** Steht im Namen des Knopfes: die Anschrift in einer Zeile. */
  readonly name: string;
  readonly group: AddressGroup;
  readonly offen: boolean;
  readonly busy: boolean;
  /** Solange "Erneut versuchen" steht, ist das die eine Hauptaktion (NFR-UX-004). */
  readonly zuruecktreten: boolean;
  /** Die Feldantwort des Servers, nur solange dieses Formular offen ist. */
  readonly feldfehler: readonly FieldError[];
  readonly onOeffnen: () => void;
  readonly onAbbrechen: () => void;
  readonly onSpeichern: (eingabe: KorrekturEingabe) => void;
}): ReactElement {
  const pflege = usePflegeStyles();
  const own = useStyles();
  const titelId = useId();
  const laender = useMemo(() => countryOptions(), []);
  const [eingabe, setEingabe] = useState<KorrekturEingabe>(() => correctionInputOf(group));
  const panelRef = useRef<HTMLFormElement | null>(null);
  const knopfRef = useRef<HTMLDivElement | null>(null);
  const warOffen = useRef(false);
  const abgebrochen = useRef(false);
  useEffect(() => {
    if (offen && !warOffen.current) {
      // Jedes Oeffnen beginnt mit der GESPEICHERTEN Fassung, nicht mit einem
      // verworfenen Entwurf.
      setEingabe(correctionInputOf(group));
      panelRef.current?.querySelector<HTMLElement>('select, input')?.focus();
    } else if (!offen && warOffen.current && abgebrochen.current) {
      knopfRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
    warOffen.current = offen;
    abgebrochen.current = false;
  }, [offen, group]);

  if (!offen) {
    return (
      <div className={pflege.zeilenaktionen} ref={knopfRef}>
        {/* Der sichtbare Text steht VORN im Namen (WCAG 2.5.3): "Anschrift
            korrigieren" trifft auch per Sprachsteuerung. */}
        <Button
          appearance="secondary"
          size="small"
          className={pflege.zeilenknopf}
          disabled={busy}
          aria-label={`Anschrift korrigieren: ${name}`}
          onClick={onOeffnen}
        >
          Anschrift korrigieren
        </Button>
      </div>
    );
  }

  const fehlerFuer = (fieldPath: string): string | null => {
    const fehler = feldfehler.find((eintrag) => eintrag.fieldPath === fieldPath);
    return fehler === undefined ? null : validationMessageFor(fieldPath, fehler.reasonCode);
  };
  const setze = (feld: keyof KorrekturEingabe) => (wert: string) => setEingabe((vorher) => ({ ...vorher, [feld]: wert }));
  const plzPflicht = eingabe.countryCode === POSTAL_CODE_REQUIRED_COUNTRY;
  const mehrere = group.usages.length > 1;
  const zwecke = group.usages.map((usage) => usageLabel(usage.usageCode)).join(', ');

  return (
    // Ein eigenes Formular: Enter im letzten Feld speichert, wie oben beim Hinzufuegen.
    <div role="group" aria-labelledby={titelId}>
      <form
        className={pflege.beenden}
        ref={panelRef}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSpeichern(eingabe);
        }}
      >
        <Body1 id={titelId}>
          <strong>Anschrift korrigieren</strong>
        </Body1>
        <Caption1>
          {mehrere
            ? `Für einen Tippfehler oder eine fehlende Angabe. Die Korrektur gilt für alle Zwecke dieser Anschrift (${zwecke}).`
            : 'Für einen Tippfehler oder eine fehlende Angabe.'}{' '}
          Die bisherigen Angaben werden nicht gelöscht; eine falsche Korrektur behebst du mit einer weiteren. Bei einem Umzug
          setzt du stattdessen die Anschrift oder den einzelnen Zweck auf „Nicht mehr verwenden“.
        </Caption1>
        <Field label="Land" required validationMessage={fehlerFuer('countryCode')}>
          <Select className={own.land} value={eingabe.countryCode} onChange={(_event, data) => setze('countryCode')(data.value)}>
            {laender.map((land) => (
              <option key={land.code} value={land.code}>
                {land.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Straße und Hausnummer" required validationMessage={fehlerFuer('addressLine1')}>
          <Input
            className={pflege.feld}
            autoComplete="address-line1"
            value={eingabe.addressLine1}
            onChange={(_event, data) => setze('addressLine1')(data.value)}
          />
        </Field>
        <Field label="Adresszusatz" hint="Kann leer bleiben." validationMessage={fehlerFuer('addressLine2')}>
          <Input
            className={pflege.feld}
            autoComplete="address-line2"
            value={eingabe.addressLine2}
            onChange={(_event, data) => setze('addressLine2')(data.value)}
          />
        </Field>
        <Field
          label={plzPflicht ? 'Postleitzahl' : 'Postleitzahl (falls vorhanden)'}
          required={plzPflicht}
          validationMessage={fehlerFuer('postalCode')}
        >
          <Input
            className={pflege.feld}
            autoComplete="postal-code"
            value={eingabe.postalCode}
            onChange={(_event, data) => setze('postalCode')(data.value)}
          />
        </Field>
        <Field label="Ort" required validationMessage={fehlerFuer('locality')}>
          <Input
            className={pflege.feld}
            autoComplete="address-level2"
            value={eingabe.locality}
            onChange={(_event, data) => setze('locality')(data.value)}
          />
        </Field>
        <div className={pflege.zeilenaktionen}>
          <Button appearance={zuruecktreten ? 'secondary' : 'primary'} disabled={busy} type="submit">
            Korrektur speichern
          </Button>
          <NebenKnopf
            disabled={busy}
            onClick={() => {
              abgebrochen.current = true;
              onAbbrechen();
            }}
          >
            Abbrechen
          </NebenKnopf>
        </div>
      </form>
    </div>
  );
}
